import type { SkullOwnerNbt } from "../models/itemStack";
import { sfetchStandalone } from "../hooks/sfetch";

export interface ResolvedSkin {
  skinUrl: string;
  textureHash: string;
  uuid?: string;
  name?: string;
  model: "classic" | "slim";
}

interface DecodedTexture {
  skinUrl?: string;
  model?: "classic" | "slim";
  profileName?: string;
  profileId?: string;
}

function toHttps(url: string): string {
  return url.replace(/^http:\/\//i, "https://");
}

function hashFromUrl(url: string): string {
  return url.slice(url.lastIndexOf("/") + 1);
}

function decodeTextureValue(value: string): DecodedTexture {
  let decoded: string;
  try {
    decoded = atob(value);
  } catch {
    return {};
  }

  try {
    const json = JSON.parse(decoded);
    const skin = json?.textures?.SKIN;
    if (skin?.url) {
      return {
        skinUrl: toHttps(String(skin.url)),
        model: skin.metadata?.model === "slim" ? "slim" : "classic",
        profileName: typeof json.profileName === "string" ? json.profileName : undefined,
        profileId: typeof json.profileId === "string" ? json.profileId : undefined,
      };
    }
  } catch {
    // The texture property is not always strict JSON (keys may be unquoted); fall through to regex.
  }

  const urlMatch = decoded.match(/"?url"?\s*:\s*"([^"]+)"/i);
  if (!urlMatch) return {};
  const model = /"?model"?\s*:\s*"?slim"?/i.test(decoded) ? "slim" : "classic";
  return { skinUrl: toHttps(urlMatch[1]), model };
}

const cache = new Map<string, Promise<ResolvedSkin | null>>();

async function lookup(path: string): Promise<ResolvedSkin | null> {
  const res = await sfetchStandalone(path);
  if (!res.ok || !res.value?.skinUrl) return null;
  const v = res.value;
  return {
    skinUrl: toHttps(String(v.skinUrl)),
    textureHash: v.skinHash ?? hashFromUrl(String(v.skinUrl)),
    uuid: v.uuid,
    name: v.name,
    model: v.model === "slim" ? "slim" : "classic",
  };
}

export function resolveSkin(owner: SkullOwnerNbt | undefined): Promise<ResolvedSkin | null> {
  if (!owner) return Promise.resolve(null);

  if (owner.textureValue) {
    const decoded = decodeTextureValue(owner.textureValue);
    if (decoded.skinUrl) {
      return Promise.resolve({
        skinUrl: decoded.skinUrl,
        textureHash: hashFromUrl(decoded.skinUrl),
        uuid: owner.id ?? decoded.profileId,
        name: owner.name ?? decoded.profileName,
        model: decoded.model ?? "classic",
      });
    }
  }

  const key = owner.id ? `uuid:${owner.id}` : owner.name ? `name:${owner.name.toLowerCase()}` : null;
  if (!key) return Promise.resolve(null);

  let pending = cache.get(key);
  if (!pending) {
    const path = owner.id
      ? `/api/playerprofile/by-uuid/${encodeURIComponent(owner.id)}`
      : `/api/playerprofile/by-name/${encodeURIComponent(owner.name!)}`;
    pending = lookup(path);
    cache.set(key, pending);
  }
  return pending;
}
